import { Wind, MessageCircle, Mail, MapPin, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { business } from "@/config/business";
import { getWhatsAppUrl } from "@/lib/whatsapp";
import { scrollToId } from "@/lib/scroll";
import { services } from "@/data/services";
import { trackEvent, EVENTS } from "@/lib/tracking";

const NAV = [
  { label: "Layanan", target: "services" },
  { label: "Solusi B2B", target: "industries" },
  { label: "Tentang Kami", target: "why-choose-us" },
  { label: "Proses", target: "process" },
  { label: "Portfolio", target: "portfolio" },
  { label: "FAQ", target: "faq" },
];

// Halaman layanan (SEO) untuk internal linking & crawlability.
const SERVICE_PAGES = [
  { label: "Service & Maintenance AC", to: "/ac" },
  { label: "Maintenance HVAC", to: "/maintenance-hvac" },
  { label: "Maintenance AC Kantor", to: "/maintenance-ac-kantor" },
  { label: "Maintenance AC Gedung", to: "/maintenance-ac-gedung" },
  { label: "Preventive Maintenance", to: "/preventive-maintenance" },
  { label: "Kontrak Maintenance", to: "/maintenance-contract" },
  { label: "Cuci & Service AC Rumah", to: "/service-ac-rumah" },
];

// Ikon TikTok sederhana (lucide belum punya TikTok resmi)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.5 3c.3 2 1.6 3.6 3.5 4v2.4c-1.3 0-2.5-.3-3.5-.9v6.1c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6c.3 0 .7 0 1 .1v2.5c-.3-.1-.7-.2-1-.2-1.9 0-3.5 1.6-3.5 3.6S9.1 18 11 18s3.5-1.6 3.5-3.6V3h2z" />
  </svg>
);

export const Footer = () => {
  const hasInstagram = Boolean(business.socialLinks.instagram);
  const hasTiktok = Boolean(business.socialLinks.tiktok);

  return (
    <footer data-testid="footer" className="bg-[#0B1120] text-slate-300">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_0.9fr_1.1fr_1fr]">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E0A400] text-[#0F172A]">
                <Wind className="h-5 w-5" />
              </span>
              <span className="text-xl font-extrabold tracking-tight text-white">
                SEJUKARA<span className="text-[#E0A400]">.</span>
              </span>
            </div>
            <p className="mt-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#E0A400]">
              {business.brand.tagline}
            </p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              {business.brand.byline}
            </p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              {business.description}
            </p>

            <div className="mt-6 flex gap-3">
              {hasInstagram && (
                <a href={business.socialLinks.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 transition-colors hover:bg-white/10">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {hasTiktok && (
                <a href={business.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok" className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 transition-colors hover:bg-white/10">
                  <TikTokIcon className="h-5 w-5" />
                </a>
              )}
              {!hasInstagram && !hasTiktok && (
                <span className="text-xs text-slate-500">
                  {/* Placeholder editable: tambahkan link Instagram & TikTok di src/config/business.js */}
                  [Tautan Instagram &amp; TikTok dapat ditambahkan]
                </span>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Navigasi</h3>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => (
                <li key={n.target}>
                  <button
                    type="button"
                    onClick={() => scrollToId(n.target)}
                    data-testid={`footer-nav-${n.target}`}
                    className="text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Halaman Layanan</h3>
            <ul className="mt-5 space-y-3">
              {SERVICE_PAGES.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    data-testid={`footer-page-${p.to.replace(/\//g, "")}`}
                    className="text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Kontak</h3>
            <ul className="mt-5 space-y-4">
              <li>
                <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { source: "footer" })} data-testid="footer-whatsapp" className="flex items-start gap-3 text-sm text-slate-400 transition-colors hover:text-white">
                  <MessageCircle className="mt-0.5 h-4 w-4 flex-none text-[#25D366]" />
                  <span>WhatsApp<br /><span className="font-semibold text-slate-200">{business.whatsapp.display}</span></span>
                </a>
              </li>
              <li>
                <a href={`mailto:${business.email}`} data-testid="footer-email" className="flex items-start gap-3 text-sm text-slate-400 transition-colors hover:text-white">
                  <Mail className="mt-0.5 h-4 w-4 flex-none text-[#25D366]" />
                  <span>Email<br /><span className="font-semibold text-slate-200">{business.email}</span></span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-slate-400">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-[#25D366]" />
                <span>Alamat<br /><span className="font-semibold text-slate-200">{business.address}</span><br />{business.city}, {business.region}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} {business.companyName}. Semua hak dilindungi.
          </p>
          <p className="text-xs text-slate-500">
            {business.city}, {business.region}, {business.country}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
